import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, TextField, Button, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    Stack, Tooltip, Accordion, AccordionSummary, AccordionDetails, Chip
} from "@mui/material";
import {
    Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon,
    Edit as EditIcon, RemoveCircleOutline, ExpandMore as ExpandMoreIcon,
    Class as ClassIcon
} from "@mui/icons-material";
import {
    collection, getDocs, doc, query, where, orderBy, deleteDoc, getDoc, writeBatch
} from "firebase/firestore";
import { db, auth } from "../../../firebase";
import { toast } from "react-toastify";

function FeeHeadClassAssign() {
    const [schoolId, setSchoolId] = useState("");
    const [courses, setCourses] = useState([]);
    const [feeheads, setFeeheads] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [selectedClass, setSelectedClass] = useState(null);
    const [feeRows, setFeeRows] = useState([{ feeHead: null, amount: "" }]);

    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (uid) fetchSchoolId(uid);
    }, []);

    useEffect(() => {
        if (schoolId) fetchData();
    }, [schoolId]);

    const fetchSchoolId = async (uid) => {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) setSchoolId(userDoc.data().schoolId);
    };

    const fetchData = async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const fhQ = query(collection(db, 'feeheads'), where('schoolId', '==', schoolId), orderBy('sequence_no', 'asc'));
            const cQ = query(collection(db, 'courses'), where('schoolId', '==', schoolId), orderBy('sequence', 'asc'));
            const aQ = query(collection(db, 'feeHeadAssignments'), where('schoolId', '==', schoolId));

            const [fhSnap, cSnap, aSnap] = await Promise.all([
                getDocs(fhQ), getDocs(cQ), getDocs(aQ)
            ]);

            setFeeheads(fhSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setCourses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAssignments(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            toast.error("Error loading data");
        }
        setLoading(false);
    };

    // Filter assignments for a specific course
    const getFeesForCourse = (courseId) => {
        return assignments
            .filter(a => a.classId === courseId)
            .sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0));
    };

    const handleEdit = (course, fees) => {
        setIsEdit(true);
        setSelectedClass(course);
        const rows = fees.map(f => ({
            feeHead: feeheads.find(fh => fh.id === f.feeHeadId),
            amount: f.amount
        }));
        setFeeRows(rows.length > 0 ? rows : [{ feeHead: null, amount: "" }]);
        setOpen(true);
    };

    const handleSaveBatch = async () => {
        if (!selectedClass || feeRows.some(row => !row.feeHead || !row.amount)) {
            return toast.warning("Please fill all fields");
        }
        setLoading(true);
        const batch = writeBatch(db);
        try {
            feeRows.forEach((row) => {
                const docId = `${selectedClass.id}_${row.feeHead.id}`;
                batch.set(doc(db, "feeHeadAssignments", docId), {
                    schoolId,
                    classId: selectedClass.id,
                    className: selectedClass.name,
                    feeHeadId: row.feeHead.id,
                    feeHeadName: row.feeHead.fee_heads,
                    amount: Number(row.amount),
                    sequence_no: row.feeHead.sequence_no || 0,
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            });
            await batch.commit();
            toast.success("Structure updated");
            handleClose();
            fetchData();
        } catch (err) { toast.error("Save failed"); }
        setLoading(false);
    };

    const handleClose = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedClass(null);
        setFeeRows([{ feeHead: null, amount: "" }]);
    };

    const addRow = () => setFeeRows([...feeRows, { feeHead: null, amount: "" }]);
    const removeRow = (i) => setFeeRows(feeRows.filter((_, idx) => idx !== i));
    const handleRowChange = (i, f, v) => {
        const list = [...feeRows];
        list[i][f] = v;
        setFeeRows(list);
    };

    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="700">Class-wise Fee Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                    Assign Fees
                </Button>
            </Stack>

            {courses.map((course) => {
                const courseFees = getFeesForCourse(course.id);
                const totalAmount = courseFees.reduce((sum, curr) => sum + Number(curr.amount), 0);

                return (
                    <Accordion key={course.id} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                <ClassIcon color="action" />
                                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>{course.name}</Typography>
                                <Chip 
                                    label={`Total: ₹${totalAmount.toLocaleString()}`} 
                                    color={totalAmount > 0 ? "primary" : "default"} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ mr: 2 }}
                                />
                                <Tooltip title="Quick Edit">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(course, courseFees); }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: '#fcfcfc', borderTop: '1px solid #eee' }}>
                            {courseFees.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Fee Particulars</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {courseFees.map((fee) => (
                                                <TableRow key={fee.id} hover>
                                                    <TableCell>{fee.feeHeadName}</TableCell>
                                                    <TableCell>₹{fee.amount.toLocaleString()}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" color="error" onClick={() => {
                                                            if (window.confirm("Delete this fee head?")) {
                                                                deleteDoc(doc(db, "feeHeadAssignments", fee.id)).then(fetchData);
                                                            }
                                                        }}>
                                                            <DeleteIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2 }}>
                                    No fees assigned to this class yet.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Assignment Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {isEdit ? `Edit Fees: ${selectedClass?.name}` : "New Fee Assignment"}
                </DialogTitle>
                <DialogContent dividers>
                    <Autocomplete
                        options={courses}
                        disabled={isEdit}
                        getOptionLabel={(o) => o.name || ""}
                        value={selectedClass}
                        onChange={(_, v) => setSelectedClass(v)}
                        renderInput={(p) => <TextField {...p} label="Select Class" variant="standard" sx={{ mb: 3 }} />}
                    />
                    {feeRows.map((row, i) => (
                        <Stack key={i} direction="row" spacing={1} mb={2}>
                            <Autocomplete
                                sx={{ flex: 2 }}
                                options={feeheads}
                                getOptionLabel={(o) => o.fee_heads || ""}
                                value={row.feeHead}
                                onChange={(_, v) => handleRowChange(i, 'feeHead', v)}
                                renderInput={(p) => <TextField {...p} label="Fee Head" size="small" />}
                            />
                            <TextField
                                sx={{ flex: 1 }}
                                label="Amount"
                                type="number"
                                size="small"
                                value={row.amount}
                                onChange={(e) => handleRowChange(i, 'amount', e.target.value)}
                            />
                            <IconButton onClick={() => removeRow(i)} disabled={feeRows.length === 1} color="error">
                                <RemoveCircleOutline />
                            </IconButton>
                        </Stack>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Add Fee Head</Button>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveBatch} disabled={loading}>
                        Save Structure
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default FeeHeadClassAssign;